import { useEffect, useState } from 'react';
import { DESKTOP_LAYOUT_MEDIA_QUERY } from '@/hooks/useMediaQuery';
import { isPhoneNavigation } from './usePhoneNavigation';

/**
 * I-2453: what only a narrow window draws (`phoneShell.ts`: the phone rows — the top row, the back row,
 * the page top row and the search field — the browser's bottom bar and the phone menu) is one load. A
 * launch in a narrow window awaits it before the first render (`index.tsx`), so all of it is on the
 * first frame and nothing arrives under the page and moves it; a desktop launch never loads it
 * (`check-first-screen-chunks.mjs` holds the entry to that). A window narrowed later loads it then.
 */
type PhoneChromeModuleType = typeof import('./phoneShell');

let loaded: PhoneChromeModuleType | null = null;
let loading: Promise<PhoneChromeModuleType> | null = null;

export function loadPhoneChrome(): Promise<PhoneChromeModuleType> {
  loading ??= import('./phoneShell').then(
    (module) => (loaded = module),
    (error: unknown) => {
      // A failed chunk is asked for again by the next caller rather than remembered.
      loading = null;
      throw error;
    },
  );
  return loading;
}

/**
 * Whether this launch draws any of it: phone mode, read the way `usePhoneNavigation` reads it, or a
 * window narrower than the desktop layout (the bottom bar's own `desktop:hidden`, the desktop app too).
 */
export function launchesNarrow(): boolean {
  const wide = window.matchMedia(DESKTOP_LAYOUT_MEDIA_QUERY).matches;
  return !wide || isPhoneNavigation(wide);
}

/**
 * The module while `wanted` (a narrow window, or phone mode), or `null` until it is here. Loaded at a
 * narrow launch, it is here on the first render.
 */
export function usePhoneChromeModule(wanted: boolean): PhoneChromeModuleType | null {
  const [module, setModule] = useState(loaded);
  useEffect(() => {
    if (!wanted || module) return;
    let live = true;
    loadPhoneChrome().then(
      (next) => {
        if (live) setModule(next);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [wanted, module]);
  return module ?? loaded;
}
